import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Budget, BudgetInsert, BudgetUpdate, BudgetLine, BudgetLineInsert, BudgetLineUpdate, BudgetCallFrequency, BudgetType } from '../types'

// Type pour les suggestions d'appels de fonds
export interface BudgetCallSuggestion {
  budget_id: string
  residence_id: string
  fiscal_year_id: string
  budget_name: string
  budget_type: BudgetType
  total_amount: number
  call_frequency: BudgetCallFrequency
  calls_per_year: number
  calls_done: number
  calls_remaining: number
  suggested_amount: number
  call_status: 'completed' | 'in_progress' | 'not_started'
}

const QUERY_KEY = 'budgets'
const LINES_QUERY_KEY = 'budget_lines'

export function useBudgets(residenceId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, residenceId, fiscalYearId],
    queryFn: async () => {
      let query = supabase
        .from('budgets')
        .select(`
          *,
          fiscal_year:fiscal_years(id, name, start_date, end_date)
        `)
        .order('created_at', { ascending: false })

      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }
      if (fiscalYearId) {
        query = query.eq('fiscal_year_id', fiscalYearId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Budget[]
    },
    enabled: !!residenceId,
  })
}

export function useBudget(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('budgets')
        .select(`
          *,
          fiscal_year:fiscal_years(id, name, start_date, end_date),
          budget_lines(*)
        `)
        .eq('id', id!)
        .single()

      if (error) throw error
      return data as Budget & { budget_lines: BudgetLine[] }
    },
    enabled: !!id,
  })
}

export function useBudgetLines(budgetId?: string) {
  return useQuery({
    queryKey: [LINES_QUERY_KEY, budgetId],
    queryFn: async () => {
      // Le compte de charges est déterminé automatiquement par le type de budget
      // (701 pour charges courantes, 102 pour travaux, 105 pour fonds ALUR)
      const { data, error } = await supabase
        .from('budget_lines')
        .select(`
          *,
          distribution_key:distribution_keys(*)
        `)
        .eq('budget_id', budgetId!)
        .order('sort_order')

      if (error) throw error
      return data as BudgetLine[]
    },
    enabled: !!budgetId,
  })
}

export function useCreateBudget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: BudgetInsert) => {
      const { data: result, error } = await supabase
        .from('budgets')
        .insert(data)
        .select()
        .single()

      if (error) throw error
      return result as Budget
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, data.residence_id] })
    },
  })
}

export function useUpdateBudget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: BudgetUpdate & { id: string }) => {
      const { data: result, error } = await supabase
        .from('budgets')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as Budget
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteBudget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // Supprimer d'abord les lignes de budget
      const { error: linesError } = await supabase
        .from('budget_lines')
        .delete()
        .eq('budget_id', id)

      if (linesError) throw linesError

      // Puis supprimer le budget
      const { error } = await supabase
        .from('budgets')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useCreateBudgetLine() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: BudgetLineInsert) => {
      const { data: result, error } = await supabase
        .from('budget_lines')
        .insert(data)
        .select()
        .single()

      if (error) throw error
      return result as BudgetLine
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [LINES_QUERY_KEY, data.budget_id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateBudgetLine() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: BudgetLineUpdate & { id: string }) => {
      const { data: result, error } = await supabase
        .from('budget_lines')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as BudgetLine
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [LINES_QUERY_KEY, data.budget_id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteBudgetLine() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('budget_lines')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LINES_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useVoteBudget() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: result, error } = await supabase
        .from('budgets')
        .update({
          status: 'voted',
          voted_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as Budget
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Hook pour obtenir les suggestions d'appels de fonds par budget
export function useBudgetCallSuggestions(residenceId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'call_suggestions', residenceId, fiscalYearId],
    queryFn: async () => {
      let query = supabase
        .from('budget_call_suggestions')
        .select('*')

      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }
      if (fiscalYearId) {
        query = query.eq('fiscal_year_id', fiscalYearId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as BudgetCallSuggestion[]
    },
    enabled: !!residenceId,
  })
}
